import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../../../../firebase-client';

export default function EmailTemplateLibrary({ teamId, content, onApply }) {
  const [uid, setUid] = useState(null);
  const [scope, setScope] = useState('personal');
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => onAuthStateChanged(auth, (user) => setUid(user?.uid || null)), []);
  const path = scope === 'club' ? ['teams', teamId, 'emailTemplates'] : ['teams', teamId, 'emailPersonalTemplates', uid, 'templates'];
  useEffect(() => {
    setSelectedId('');
    setName('');
    setTemplates([]);
    setError('');
    setNotice('');
    setLoading(true);
    if (!uid || !teamId) return;
    const segments = scope === 'club' ? ['teams', teamId, 'emailTemplates'] : ['teams', teamId, 'emailPersonalTemplates', uid, 'templates'];
    return onSnapshot(
      collection(db, ...segments),
      (snapshot) => {
        setTemplates(snapshot.docs.map((item) => ({ ...item.data(), id: item.id })).sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      },
      () => {
        setError('Could not load templates. Please reopen the composer to retry.');
        setLoading(false);
      },
    );
  }, [teamId, uid, scope]);
  const selected = templates.find((item) => item.id === selectedId);
  async function save(update = false) {
    if (!name.trim() || !content.subject.trim() || !content.html.trim()) {
      setError('Enter a template name, email subject and content first.');
      return;
    }
    if (content.subject.length > 998 || content.previewText.length > 500) {
      setError('Use a subject up to 998 characters and preview text up to 500 characters.');
      return;
    }
    if (content.html.length > 200000) {
      setError('Template content is too large (maximum 200,000 characters).');
      return;
    }
    if (update && !window.confirm(`Update “${selected?.name}”${scope === 'club' ? ' for all club communications admins' : ''}?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const reference = update ? doc(db, ...path, selectedId) : doc(collection(db, ...path));
      await setDoc(reference, {
        name: name.trim(),
        subject: content.subject,
        previewText: content.previewText,
        html: content.html,
        updatedAt: serverTimestamp(),
      });
      setSelectedId(reference.id);
      setNotice('Template saved.');
    } catch {
      setError('Could not save the template. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (
      !selected ||
      !window.confirm(`Delete “${selected.name}”${scope === 'club' ? ' for all club communications admins' : ''}? Emails already sent are unaffected.`)
    )
      return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await deleteDoc(doc(db, ...path, selectedId));
      setSelectedId('');
      setName('');
      setNotice('Template deleted.');
    } catch {
      setError('Could not delete the template. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="email-template-library">
      <summary>Reusable templates</summary>
      <p className="text-muted">Save the subject, preview text and content. Audience and delivery settings stay with this email.</p>
      <div className="email-composer-grid">
        <label>
          <span className="form-label">Template library</span>
          <select value={scope} disabled={busy} onChange={(event) => setScope(event.target.value)}>
            <option value="personal">My templates</option>
            <option value="club">Club templates</option>
          </select>
        </label>
        <label>
          <span className="form-label">Saved template</span>
          <select
            value={selectedId}
            disabled={busy || loading}
            onChange={(event) => {
              setSelectedId(event.target.value);
              setName(templates.find((item) => item.id === event.target.value)?.name || '');
              setNotice('');
            }}
          >
            <option value="">{loading ? 'Loading templates…' : 'Choose a template'}</option>
            {templates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-muted">
        {scope === 'club'
          ? 'Club templates can be used and edited by admins with communications permission.'
          : 'My templates are private to you within this club.'}
      </p>
      <div className="email-composer-actions">
        <button
          type="button"
          className="btn btn--secondary"
          disabled={!selected || busy}
          onClick={() => {
            if (window.confirm('Replace the current subject, preview text and content with this template?')) onApply(selected);
          }}
        >
          Use template
        </button>
        <button type="button" className="btn btn--ghost" disabled={!selected || busy} onClick={remove}>
          Delete template
        </button>
      </div>
      <label>
        <span className="form-label">Template name</span>
        <input value={name} maxLength={100} onChange={(event) => setName(event.target.value)} placeholder="e.g. Weekly newsletter" />
      </label>
      <div className="email-composer-actions">
        <button type="button" className="btn btn--secondary" disabled={busy || loading || !uid} onClick={() => save()}>
          Save as new template
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy || loading || !selected || !uid} onClick={() => save(true)}>
          Update selected template
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
    </details>
  );
}
